<template>
<!-- WHAT: A components to collect the date-metrics, date-date and date-dim models data (Copied from BtDimsMetricsModel).
   HISTORY:
   * V251104.1: Fixed a 2-way binding issue by consuming the initial-model prop from BtMetricsModel.
   * V251027.1: Supported 2-way binding: Initialized form fields from v-model.
   * V250807.1: Added hasFilter prop and passed it to bt-metrics-model.
   * V250708.1: Added support for the date-dim model + Added chip to pivotDimIn.
   * V250707.1: Removed initialMetrics prop in order to allow all metrics and on columns. Instead, used noLabel prop to hide the unnecessary label.
   * V250703.1: Validated dateParts to always have 2 items + Consumed BtMetricsModel's initialMetrics prop + Added quarter to dateParts.
   * V250702.2: 1st release.
-->
<v-form lazy-validation
   ref="form"
   v-model="isFormValid"
>
   <v-row class="px-2 pb-2">
      <v-col xs="12" sm="12" :md="colSize" class="py-0">
         <v-autocomplete persistent-hint required
            ref="dateField"
            autocomplete="off"
            class="py-0"
            placeholder="select a date column..."
            hint="* Date Column"
            :items="dateColumns"
            :rules="[rules.required]"
            v-model="dateField"
            @change="dateFieldChanged"
         ></v-autocomplete>
      </v-col>
      <v-col xs="12" sm="12" :md="colSize" class="pt-1 pb-0">
         <v-select dense hide-selected multiple persistent-hint required return-object deletable-chips small-chips
            ref="dateParts"
            autocomplete="off"
            placeholder="select one or more date parts..."
            hint="* Date Parts"
            :items="datePartsItems"
            :rules="datePartsRules"
            v-model="dateParts"
            @change="datePartsChanged"
         ></v-select>
      </v-col>
      <v-col v-if="type === 'date-dim'"
         xs="12" sm="12" :md="colSize - 1" class="py-0"
      >
         <v-autocomplete persistent-hint required
            ref="pivotDim"
            autocomplete="off"
            class="py-0"
            placeholder="select the pivot column..."
            hint="* Pivot Column"
            :items="varcharColumns"
            :rules="[rules.required]"
            v-model="pivotDim"
            @change="pivotDimChanged"
         ></v-autocomplete>
      </v-col>
      <v-col v-if="type === 'date-dim'"
         xs="12" sm="12" :md="colSize + 1" class="pt-1 pb-0"
      >
         <v-autocomplete multiple persistent-hint required deletable-chips small-chips
            ref="pivotDimIn"
            autocomplete="off"
            class="py-0"
            placeholder="select one or more value..."
            hint="* Pivot Column Values"
            :counter="pivotDimInItems.length"
            :disabled="loadingPivotDimIn"
            :items="pivotDimInItems"
            :loading="loadingPivotDimIn"
            :rules="[rules.requiredArray]"
            v-model="pivotDimIn"
            @change="pivotDimInChanged"
         ></v-autocomplete>
      </v-col>
   </v-row>
   <v-row>
      <v-col cols="12" class="pb-0">
            <!-- :initial-metrics="initialMetrics" -->
         <bt-metrics-model
            :has-filter="type === 'date-metrics'"
            :initial-model="initialModel"
            :max="type === 'date-metrics' ? undefined : 1"
            :no-label="type != 'date-metrics'"
            :schemas="schemas"
            :query-submitted="querySubmitted"
            v-model="metricsData"
            @form-valid="metricsDataChanged"
         />
      </v-col>
   </v-row>
</v-form>
</template>

<script>
import BtMetricsModel from './BtMetricsModel.vue';
import { isEmpty } from '../mixins/bt-mixin.js';

const NAME = "BtDatesModel";
const VERSION = 'V251104.1';
const DATE_PARTS = [
   { text: 'Year', value: 'year', seq: 1 },
   { text: 'Quarter', value: 'quarter', seq: 2 },
   { text: 'Month', value: 'month', seq: 3 },
   { text: 'Day', value: 'day', seq: 4 },
   { text: 'Hour', value: 'hour', seq: 5 }
];

export default {
   name: NAME,

   components: {
      BtMetricsModel
   },

   props: {
      value: {
         type: Object,
         default: () => ({})
      },
      debug: {
         type: Boolean,
         default: false
      },
      hasFilter: {
         type: Boolean,
         default: false
      },
      initialModel: {
         type: Boolean
      },
      pivotDimInItems: {
         type: Array,
         default: () => []
      },
      querySubmitted: {
         type: Boolean,
         required: true
      },
      schemas: {
         type: Array,
         required: true,
         default: () => []
      },
      type: {
         type: String,
         required: true
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || 'Value is required!',
            requiredArray: value => !!value.length || "Value is required!",
            dateParts: value => value.length === 2 || 'You must select exactly 2 items!'
         },
         isFormValid: false,
         isMetricsModelValid: true,
         // isFormChanged: false,
         dateColumns: [],
         dateField: '',
         datePartsItems: [],
         dateParts: [],
         pivotDim: '',
         varcharColumns: [],
         pivotDimIn: [],
         loadingPivotDimIn: false,
         metricsData: {},
         isInit: false
      }
   },

   computed: {
      // initialMetrics() {
      //    if (this.type === 'date-date')
      //       return [{
      //          metric: 'count',
      //          on: '*',
      //          label: 'Count'
      //       }];
      //    else return undefined
      // },

      colSize() {
         return this.type === 'date-dim' ? 3 : 6;
      },

      datePartsRules() {
         const rules = [this.rules.requiredArray];
         if (this.type === 'date-date')
            rules.push(this.rules.dateParts);

         return rules;
      }
   },

   watch: {
      initialModel: {
         immediate: true,
         handler(val) {
            if (val) this.init();
         }
      },

      schemas: {
         immediate: true,
         deep: true,
         async handler(val) {
            // this.alert('in ' + NAME + '/watch: schemas=' + JSON.stringify(val));
            // // this.dateField = '';
            // // this.dateParts = [];
            // // this.formChanged();
            if (val?.length) {
               this.dateColumns = val.filter(col => col.column_type === 'DATE' || col.column_type === 'TIMESTAMP')
                  .map(col => col.column_name);
               this.varcharColumns = val.filter(col => col.column_type === 'VARCHAR')
                  .map(col => col.column_name);
            } else {
               this.dateColumns = [];
               this.varcharColumns = [];
            }

            // this.datePartsChanged();
            // this.isFormChanged = false;

            this.init();

            this.$nextTick(() => {
               // // this.formChanged();
               this.$refs.dateField?.focus();
            });
         },

         // querySubmitted: {
         //    handler(val) {
         //       if (val) this.isFormChanged = false;
         //    }
         // }
      },

      type: {
         handler() {
            this.datePartsChanged(this.dateParts);
         }
      },

      pivotDimInItems: {
         async handler(items) {
            if (this.isInit) {
               this.isInit = false;
               this.pivotDimIn = this.value.pivotDimIn?.filter(val =>
                  items.some(item => item === val)
               );
            }

            this.loadingPivotDimIn = false;
         }
      }
   },

   methods: {
      alert(msg) {
         if (this.debug) {
            alert(`${NAME} ${VERSION} says:\n${msg}`);
         }
      },

      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} ${VERSION} says => ${msg}`);
         }
      },

      init() {
         if (isEmpty(this.value) || !this.varcharColumns?.length || !this.dateColumns?.length) {
            this.dateField = '';
            this.dateParts = [];
            this.pivotDim = '';
            this.metricsData = {};
            this.isMetricsModelValid = false;
         } else {
            const { dateField, dateParts, pivotDim, metrics, whereClause } = this.value;

            if (this.type === 'date-dim') {
               this.pivotDim = this.varcharColumns.includes(pivotDim) ? pivotDim : '';
               if (this.pivotDim) {
                  this.isInit = true;
                  this.getPivotDimInItems(pivotDim);                  
               } else {
                  this.pivotDimIn = [];
               }
            }

            this.dateField = this.dateColumns.includes(dateField) ? dateField : '';
            this.dateParts = DATE_PARTS.filter(item => dateParts.includes(item.value));
            
            this.metricsData = {
               metrics: [...metrics],
               ...(whereClause && { whereClause: whereClause })
            }

            this.isMetricsModelValid = true;
         }

         this.datePartsChanged(this.dateParts);
      },

      dateFieldChanged() {
         this.$refs.dateParts.focus();
         this.formChanged();
      },

      datePartsChanged(val) {
         // this.dimsErrMsg = val.length ? '' : 'Value is required!';
         if (!val?.length)
            this.datePartsItems = DATE_PARTS;
         else {
            // const minSeq = Math.min(...val.map(item => item.seq));
            // this.datePartsItems = DATE_PARTS.filter(item => item.seq >= minSeq);
            const arrSeq = val.map(item => item.seq);
            this.datePartsItems = DATE_PARTS.filter(item => arrSeq.includes(item.seq));

            if (this.type === 'date-date' && val.length >= 2) {
               // this.datePartsItems.forEach(item => {
               //    item.disabled = item.seq > lastSeq;
               // });
            } else {
               const lastSeq = arrSeq[arrSeq.length - 1];
               // this.datePartsItems = DATE_PARTS.filter(item => arrSeq.includes(item.seq) || item.seq > lastSeq);
               this.datePartsItems.push(...DATE_PARTS.filter(item => item.seq > lastSeq));
            }
         }

         this.formChanged();
      },

      pivotDimChanged(val) {
         // this.loadingPivotDimIn = true;
         // this.$emit('pivot-dim-change', val);
         this.pivotDimIn = [];
         this.getPivotDimInItems(val);
         this.formChanged();
      },

      getPivotDimInItems(pivotDim) {
         this.loadingPivotDimIn = true;
         this.$emit('pivot-dim-change', pivotDim);
      },

      pivotDimInChanged() {
         this.formChanged();
      },

      metricsDataChanged(val) {
         // alert('in metricsDataChanged(): val=' + val);
         this.isMetricsModelValid = val;
         this.formChanged();
      },

      formChanged() {
         let emitVal = false;
         this.isFormValid = this.$refs.form?.validate();
         if (this.isFormValid && this.isMetricsModelValid) {
            const modelData = {
               dateField: this.dateField,
               dateParts: this.dateParts.map(item => item.value),
               ...this.metricsData
            };

            if (this.type === 'date-dim') {
               modelData.pivotDim = this.pivotDim;
               modelData.pivotDimIn = this.pivotDimIn;
               modelData.pivotDimQuote = true;
            }

            this.$emit('input', modelData);
            emitVal = true;
         }

         this.$emit('form-valid', emitVal);
      }
   },

   mounted() {
      // alert(`in ${NAME}.mounted(): `);
      if (this.initialModel === undefined)
         this.init();
   }
}
</script>

<style scoped>
</style>